/**
 * WEB-4239
 * Written by the post-commit page when a payment has been confirmed as approved, and read
 * by the itinerary payment modals. `jetsmart_payment_success` cannot be used for this:
 * post-commit deletes that entry before navigating to the itinerary.
 */
export const PAYMENT_APPROVED_STORAGE_KEY = "jetsmart_payment_approved";

/**
 * How long an approval keeps the itinerary payment modals hidden. Navitaire needs a moment
 * to show the payment as settled; this covers that window. Matches the grace period
 * already used by PaymentRetryModal.
 */
export const PAYMENT_APPROVED_GRACE_MS = 5 * 60 * 1000;

/**
 * Written by usePendingPaymentRevalidation when its own background poll (SafetyPay and other
 * async methods) resolves to DECLINED after the customer is already sitting on the itinerary
 * page. Mirrors PAYMENT_APPROVED_STORAGE_KEY so the retry modal and transactions tab can react
 * without the customer having to reload.
 */
export const PAYMENT_DECLINED_STORAGE_KEY = "jetsmart_payment_declined_itinerary";

/**
 * Namespaces a payment storage key by tab. localStorage is shared across every tab of the same
 * origin, so without this, two tabs mid-checkout on different bookings at the same time would
 * clobber each other's marker - the second tab's write would silently overwrite the first's.
 * Falls back to the bare key when no tab session id is available, so the marker still works
 * (just unscoped) rather than being dropped outright.
 */
export function withTabSession(baseKey: string, tabSessionId: string | null | undefined): string {
  return tabSessionId ? `${baseKey}:${tabSessionId}` : baseKey;
}

/**
 * Written by the post-commit page when Yuno reports a non-terminal status (SafetyPay and other
 * async methods settle after the redirect), and read by the itinerary so it can keep checking
 * instead of stranding the customer on a stale "payment pending" screen.
 */
export const PAYMENT_PENDING_STORAGE_KEY = "jetsmart_payment_pending";

/** How long a pending marker stays actionable before the itinerary stops re-checking. */
export const PAYMENT_PENDING_GRACE_MS = 5 * 60 * 1000;

/**
 * Yuno statuses that will never change again. Anything else is treated as still settling.
 *
 * DELIBERATELY SHORT. These are the only two the codebase actually evidences as Yuno payment
 * statuses — see the provenance note in DEV_GUIDE.md. Do not pad this list from memory or from
 * adjacent enums; the two failure modes are not symmetric:
 *
 *   - A MISSING terminal status  -> we poll the full budget, then proceed with the last value.
 *                                   Slower, still correct.
 *   - A WRONG terminal status    -> we stop polling early and decide on a status that was
 *                                   still going to change. Incorrect outcome.
 *
 * Only add a value here once the BFF or Yuno team has confirmed it. Each confirmed addition
 * shortens the wait; a guess risks committing against a non-final status.
 */
export const TERMINAL_YUNO_STATUSES = ["SUCCEEDED", "DECLINED", "REJECTED"] as const;

export function isTerminalYunoStatus(status: string): boolean {
  return (TERMINAL_YUNO_STATUSES as readonly string[]).includes(status.trim().toUpperCase());
}

/** Post-commit poll budget: 10 x 2s = up to 20s, spent on the spinner the customer already sees. */
export const POST_COMMIT_POLL_ATTEMPTS = 30;
export const POST_COMMIT_POLL_INTERVAL_MS = 2000;

/** Keep the post-commit spinner up for at least this long in total, poll time included. */
export const POST_COMMIT_MIN_VISIBLE_MS = 3000;

/** Itinerary poll budget: 12 x 5s = up to 60s after the customer lands. */
export const ITINERARY_POLL_ATTEMPTS = 12;
export const ITINERARY_POLL_INTERVAL_MS = 5000;

/**
 * Navitaire's swagger has enums has 7 values New, Received, Pending, Approved, etc so we need these "approved", "declined" as terminal payment statuses.
 */
export const TERMINAL_PAYMENT_STATUSES = ["approved", "declined", "rejected"] as const;

export function isTerminalPaymentStatus(status?: string | null): boolean {
  if (!status) return false;
  return (TERMINAL_PAYMENT_STATUSES as readonly string[]).includes(status.trim().toLowerCase());
}

/**
 * The booking state status codes (info.status / info.paidStatus )
 */
export const TERMINAL_BOOKING_PAYMENT_STATUS_CODES = [2, 3, 4] as const;

export function isTerminalBookingPaymentStatusCode(status?: number | null): boolean {
  if (status === null || status === undefined) return false;
  return (TERMINAL_BOOKING_PAYMENT_STATUS_CODES as readonly number[]).includes(status);
}

/**
 * Written by ProcessingModal right before handing off to Yuno (SDK-action/3DS challenges and
 * some async methods redirect the browser through the bank/gateway, which is a hard navigation
 * that wipes in-memory Redux state - including the CUIT/CUIL the customer just entered). Read by
 * the post-commit page once the payment is confirmed, since that's the only point every Yuno
 * payment path (SPA redirect or hard redirect) reliably passes through with a settled status.
 */
export const PAYMENT_TAX_INFORMATION_STORAGE_KEY = "jetsmart_payment_tax_information";

// Sends check-in back to passenger selection (instead of the itinerary) and re-retrieves the booking there.
export const CHECKIN_RESUME_PARAM = "checkinResume";
export const CHECKIN_RESUME_QUERY = `${CHECKIN_RESUME_PARAM}=true`;

export const CUIT_CUIL_LENGTH = 11;
export const CUIT_CUIL_VALIDATION_MESSAGE = `Este campo debe tener ${CUIT_CUIL_LENGTH} caracteres de longitud`;
export const CUIT_CUIL_REQUIRED_MESSAGE = "Debes completar todos los campos";

export const ORCHESTRATOR_GUID_FIELD_KEY = "GUID::Orchestrator";

export const ARGENTINA_COUNTRY_CODE = "AR";

// The payment-method selection stored when the customer picks Yuno's CLICK_TO_PAY method
// (selections are stored lowercased).
export const CLICK_TO_PAY_PAYMENT_METHOD = "click_to_pay";

// RG 5617 (Argentina) is applied to the booking as tax code "Q1" on a passenger fare's service charges.
export const RG_5617_TAX_CODE = "Q1";

export enum PaymentMethodCode {
  GIFT_CARD = "VO",
  TEST = "DG",
}
